/**
 * Creates a semaphore that caps how many wrapped async tasks run at once.
 *
 * Tasks beyond the limit queue in FIFO order and start as slots free up. The
 * returned function never rejects on its own - it resolves or rejects with
 * whatever the wrapped task does, and always releases its slot.
 *
 * @param maxConcurrent - Maximum number of tasks allowed to run simultaneously
 * @returns A function that wraps a task and defers it until a slot is free
 *
 * @example
 * const limit = createConcurrencyLimiter(3);
 * const results = await Promise.all(urls.map(url => limit(() => fetch(url))));
 */
export function createConcurrencyLimiter(maxConcurrent: number) {
  let active = 0;
  const queue: (() => void)[] = [];
  // Bumped by reset(). A task started before a reset must not decrement the
  // count afterwards, or `active` goes negative and the cap silently loosens.
  let generation = 0;

  const release = (startedIn: number) => {
    if (startedIn !== generation) return;
    active -= 1;
    const next = queue.shift();
    if (next) next();
  };

  function limit<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const run = () => {
        active += 1;
        const startedIn = generation;

        // Start the task synchronously when a slot is free, so an unqueued task
        // behaves exactly as it would without the limiter. A synchronous throw
        // is caught here rather than by deferring to a microtask.
        let pending: Promise<T>;
        try {
          pending = task();
        } catch (error) {
          release(startedIn);
          reject(error);
          return;
        }

        // Release inside both settle handlers rather than via .finally(), which
        // would return a fresh rejected promise with no handler attached and
        // surface as an unhandled rejection.
        Promise.resolve(pending).then(
          value => {
            release(startedIn);
            resolve(value);
          },
          error => {
            release(startedIn);
            reject(error);
          },
        );
      };

      if (active < maxConcurrent) {
        run();
      } else {
        queue.push(run);
      }
    });
  }

  /**
   * Drops queued tasks and zeroes the active count.
   *
   * Only for tests: a limiter is module-level state, and a test that leaves a
   * download pending would otherwise hold its slot for every test that follows.
   */
  limit.reset = () => {
    generation += 1;
    active = 0;
    queue.length = 0;
  };

  return limit;
}
